import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
    REPORT_COMMENT_SECTION_TYPES,
    ReportComment,
    ReportCommentMessage,
    ReportCommentSectionType,
} from '@/types/report-comments';

const COMMENTABLE_SECTION_IDS = REPORT_COMMENT_SECTION_TYPES;

export type CommentableSectionId = ReportCommentSectionType;
export type CommentFilter = 'active' | 'completed' | 'all';

const commentableSectionIds = new Set<string>(COMMENTABLE_SECTION_IDS);

const parseDate = (value?: string | null) => {
    if (!value) return 0;

    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
};

function normalizeCommentSectionType(sectionType?: string | null): CommentableSectionId | null {
    if (!sectionType) return null;

    return commentableSectionIds.has(sectionType) ? (sectionType as CommentableSectionId) : null;
}

export function isCommentableSectionId(
    sectionId?: string | null,
): sectionId is CommentableSectionId {
    return Boolean(sectionId && commentableSectionIds.has(sectionId));
}

export function sortCommentMessagesByCreatedAt(
    messages: ReportCommentMessage[],
): ReportCommentMessage[] {
    return [...messages].sort(
        (left, right) => parseDate(left.created_at) - parseDate(right.created_at),
    );
}

function sortReportCommentsByUpdatedAt(comments: ReportComment[]): ReportComment[] {
    return [...comments].sort(
        (left, right) => parseDate(right.updated_at) - parseDate(left.updated_at),
    );
}

function filterCommentsByStatus(comments: ReportComment[], filter: CommentFilter): ReportComment[] {
    if (filter === 'all') return comments;

    return comments.filter((comment) => comment.status === filter);
}

export function getCommentsForReport(
    comments: ReportComment[],
    filter: CommentFilter = 'all',
): ReportComment[] {
    return sortReportCommentsByUpdatedAt(
        filterCommentsByStatus(
            comments.filter((comment) => !comment.deleted_at),
            filter,
        ),
    );
}

export function getCommentSectionLabel(
    sectionType: string | null | undefined,
    sectionTitlesById: Record<string, string>,
): string {
    if (!sectionType) {
        return 'Seção não identificada';
    }

    return sectionTitlesById[sectionType] || 'Seção não identificada';
}

export function getCommentCountsBySection(comments: ReportComment[]) {
    return comments.reduce<Partial<Record<CommentableSectionId, number>>>(
        (accumulator, comment) => {
            if (comment.deleted_at || comment.status !== 'active') {
                return accumulator;
            }

            const sectionId = normalizeCommentSectionType(comment.context.section_type);

            if (!sectionId) return accumulator;

            accumulator[sectionId] = (accumulator[sectionId] || 0) + 1;
            return accumulator;
        },
        {},
    );
}

export function getTotalActiveCommentCount(comments: ReportComment[]) {
    return comments.reduce((total, comment) => {
        if (comment.deleted_at || comment.status !== 'active') {
            return total;
        }

        return total + 1;
    }, 0);
}

export function getDefaultCommentSectionId(params: {
    currentSectionId?: string | null;
    sectionIds: CommentableSectionId[];
    comments: ReportComment[];
}) {
    const { currentSectionId, sectionIds, comments } = params;

    if (isCommentableSectionId(currentSectionId)) {
        return currentSectionId;
    }

    const countsBySection = getCommentCountsBySection(comments);
    const firstSectionWithComments = sectionIds.find(
        (sectionId) => (countsBySection[sectionId] || 0) > 0,
    );

    if (firstSectionWithComments) {
        return firstSectionWithComments;
    }

    return sectionIds[0] || null;
}

export function upsertReportCommentInList(
    comments: ReportComment[],
    nextComment: ReportComment,
): ReportComment[] {
    const existingIndex = comments.findIndex((comment) => comment.id === nextComment.id);

    if (existingIndex === -1) {
        return sortReportCommentsByUpdatedAt([...comments, nextComment]);
    }

    const nextComments = [...comments];
    nextComments[existingIndex] = nextComment;
    return sortReportCommentsByUpdatedAt(nextComments);
}

export function removeReportCommentFromList(
    comments: ReportComment[],
    commentId: string,
): ReportComment[] {
    return comments.filter((comment) => comment.id !== commentId);
}

export function formatCommentTimestamp(value?: string | null): string {
    if (!value) return '';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

export function getCommentAuthorLabel(params: {
    authorId: string;
    currentUserId?: string | null;
    currentUserName?: string | null;
}) {
    const { authorId, currentUserId, currentUserName } = params;

    if (currentUserId && authorId === currentUserId) {
        return currentUserName || 'Você';
    }

    return 'Colaborador';
}

export function getNameInitials(value?: string | null) {
    if (!value) return 'C';

    const words = value.trim().split(/\s+/).slice(0, 2).filter(Boolean);

    if (words.length === 0) return 'C';

    return words.map((word) => word[0]?.toUpperCase() || '').join('');
}
